/*
 * The hidden-image cache used by the PDF preview components.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS IS
 * ---------------------------------------------------------------------------
 * `ProjectDraftCoverImageComponent` and `SpreadComponent` both render page
 * previews by putting an `<img>` into a hidden `#hiddenImages` container,
 * keyed by a generated element id, and then copying its `src` and natural
 * dimensions onto the visible element. The hidden element is the cache: if
 * `document.getElementById(id)` finds one, no request is made.
 *
 * ---------------------------------------------------------------------------
 * WHY IT NEEDS INVALIDATING (the "preview never appears" bug)
 * ---------------------------------------------------------------------------
 * A PDF page image is not available at the moment the Resource row is created.
 * The row exists before the upload finishes, and the page-count job that
 * eventually reports `numPages` is queued with a fixed 30 second delay
 * (`docs/contracts/shared/Notifications.md` §9). So the first render attempt
 * can legitimately come back with nothing.
 *
 * Both components handled that by appending the `<img>` to the cache
 * **synchronously, before the URL had arrived** — an element with the real id
 * and an empty `src`. Every later attempt then found that element, treated it
 * as a cache hit, and never requested again. `complete` is `true` for a
 * src-less image, so the "is it loaded?" branch also passed and the code
 * happily copied an empty `src` onto the visible element. The result was a
 * preview that stayed blank until a full page reload, no matter how long the
 * user waited or what the server later published.
 *
 * So two things are needed, and they are both here rather than duplicated in
 * each component:
 *
 *   1. {@link invalidateResourceImages} — drop every cached element for a
 *      Resource, so the next refresh genuinely re-requests. Called when the
 *      RESOURCE UPDATE notification says the file is now processed.
 *   2. {@link beginImageRequest} / {@link endImageRequest} — an in-flight
 *      guard that does NOT poison the cache. These components refresh from
 *      `ngOnChanges`, which fires on every change-detection pass, so without a
 *      guard removing the placeholder would turn one blank preview into a
 *      request storm. The guard is module-level rather than per-component
 *      because the same image id is rendered by several components at once
 *      (the projects grid shows many thumbnails from one cache).
 *
 * ---------------------------------------------------------------------------
 * NO ANGULAR IMPORTS -- DELIBERATE
 * ---------------------------------------------------------------------------
 * Same reasoning as `service-data/http-error.ts`: it keeps the id-matching
 * rules reachable from the layer-0 smoke tests. Covered by
 * `tests/ResourceImageCache.smoke.ts`.
 */

/** The container every cached preview image lives in. */
export const HIDDEN_IMAGES_ELEMENT_ID = 'hiddenImages';

/**
 * Does this cached element id belong to `resourceId`?
 *
 * The two id shapes in use are `resource-<id>` (cover, one image per Resource)
 * and `resource-<id>-<page>-<dpi>` (spread, one per page/dpi combination).
 *
 * ⚠ **A plain `startsWith('resource-' + id)` is wrong** and would be a nasty
 * bug to find: `resource-12`, `resource-123` and `resource-1234` all start
 * with `resource-12`, so invalidating Resource 12 would silently drop the
 * cached previews of every Resource whose id begins with 12 — which on a busy
 * organisation is a lot of thumbnails, all of which would then re-request at
 * once. The id must match exactly, or be followed by the `-` separator.
 */
export function isElementIdForResource(elementId: string, resourceId: number): boolean {
  if (typeof elementId !== 'string' || !elementId) { return false; }
  if (typeof resourceId !== 'number' || !isFinite(resourceId) || resourceId <= 0) { return false; }
  const exact = 'resource-' + resourceId;
  return elementId === exact || elementId.indexOf(exact + '-') === 0;
}

/**
 * Drop every cached preview image for a Resource.
 *
 * Returns how many were removed, which is only used for logging — a count of
 * zero is perfectly normal (nothing cached yet, or another component already
 * invalidated it).
 */
export function invalidateResourceImages(resourceId: number): number {
  if (typeof document === 'undefined') { return 0; }
  const container = document.getElementById(HIDDEN_IMAGES_ELEMENT_ID);
  if (!container) { return 0; }

  // Copy the list first: removing while iterating a live HTMLCollection skips
  // elements.
  const doomed: Element[] = [];
  const children = container.children;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child && isElementIdForResource(child.id, resourceId)) {
      doomed.push(child);
    }
  }
  for (const element of doomed) {
    if (element.parentNode) { element.parentNode.removeChild(element); }
  }
  return doomed.length;
}

/**
 * Claim an image id for a request.
 *
 * Returns `true` when the caller should go ahead, `false` when a request for
 * the same id is already outstanding. Always pair a `true` with
 * {@link endImageRequest} — including on the error path, or that id can never
 * be requested again for the life of the page.
 */
const inFlightImageRequests = new Set<string>();

export function beginImageRequest(elementId: string): boolean {
  if (!elementId) { return false; }
  if (inFlightImageRequests.has(elementId)) { return false; }
  inFlightImageRequests.add(elementId);
  return true;
}

export function endImageRequest(elementId: string): void {
  inFlightImageRequests.delete(elementId);
}

export function isImageRequestInFlight(elementId: string): boolean {
  return inFlightImageRequests.has(elementId);
}

/** Test seam: forget every in-flight claim. Not used by application code. */
export function resetImageRequestsForTest(): void {
  inFlightImageRequests.clear();
}

